import type {
  ReviewQueueClientMetadata,
  ReviewQueueItem,
} from '@/api/generated/model'

/**
 * `none` is a trace that was read fine and really sent no header. `unknown`
 * is a trace whose body could not be read (`readable === false`, TRA-945),
 * so none of its headers can be known; it is offered only when the queue
 * has such a row, like the model filter's unknown entry.
 */
export type MetadataFilter = 'any' | 'none' | 'unknown' | `value:${string}`
export type MetadataField =
  | 'client_tenant_id'
  | 'environment'
  | 'traffic_type'

export interface ClientMetadataFilters {
  clientTenant: MetadataFilter
  environment: MetadataFilter
  trafficType: MetadataFilter
}

export type QueueClientMetadataIndex = Record<
  string,
  ReviewQueueClientMetadata
>

export const ANY_METADATA_FILTER: MetadataFilter = 'any'
export const NONE_METADATA_FILTER: MetadataFilter = 'none'
export const UNKNOWN_METADATA_FILTER: MetadataFilter = 'unknown'

export function metadataValueFilter(value: string): MetadataFilter {
  return `value:${value}`
}

export function parseMetadataFilter(value: string | null): MetadataFilter {
  if (value === NONE_METADATA_FILTER) return NONE_METADATA_FILTER
  if (value === UNKNOWN_METADATA_FILTER) return UNKNOWN_METADATA_FILTER
  if (value?.startsWith('value:') && value.length > 'value:'.length) {
    return value as MetadataFilter
  }
  return ANY_METADATA_FILTER
}

function rowValue(
  item: ReviewQueueItem,
  field: MetadataField,
  metadataByKey: QueueClientMetadataIndex,
): string | null {
  const value = metadataByKey[item.trace_key]?.[field]
  return value == null || value === '' ? null : value
}

/** A row still loading has no entry and is not unreadable; it stays in none. */
function isUnreadable(
  item: ReviewQueueItem,
  metadataByKey: QueueClientMetadataIndex,
): boolean {
  return metadataByKey[item.trace_key]?.readable === false
}

/**
 * The sorted `value:` options for a field, plus `unknown` last when at
 * least one row's trace body could not be read.
 */
export function metadataOptions(
  items: readonly ReviewQueueItem[],
  field: MetadataField,
  metadataByKey: QueueClientMetadataIndex,
): MetadataFilter[] {
  const values = new Set<string>()
  let anyUnreadable = false
  for (const item of items) {
    if (isUnreadable(item, metadataByKey)) anyUnreadable = true
    const value = rowValue(item, field, metadataByKey)
    if (value !== null) values.add(value)
  }
  const options = [...values]
    .sort((a, b) => a.localeCompare(b))
    .map(metadataValueFilter)
  if (anyUnreadable) options.push(UNKNOWN_METADATA_FILTER)
  return options
}

function matchesMetadata(
  value: string | null,
  unreadable: boolean,
  filter: MetadataFilter,
): boolean {
  if (filter === ANY_METADATA_FILTER) return true
  if (filter === UNKNOWN_METADATA_FILTER) return unreadable
  if (filter === NONE_METADATA_FILTER) return !unreadable && value === null
  return value === filter.slice('value:'.length)
}

export function filterClientMetadata(
  item: ReviewQueueItem,
  filters: ClientMetadataFilters,
  metadataByKey: QueueClientMetadataIndex,
): boolean {
  const unreadable = isUnreadable(item, metadataByKey)
  return (
    matchesMetadata(
      rowValue(item, 'client_tenant_id', metadataByKey), unreadable, filters.clientTenant,
    ) &&
    matchesMetadata(rowValue(item, 'environment', metadataByKey), unreadable, filters.environment) &&
    matchesMetadata(rowValue(item, 'traffic_type', metadataByKey), unreadable, filters.trafficType)
  )
}

/**
 * Org id → display name for every tenant in the queue (TRA-1077). The
 * name is X-Tenant-Name, stored on trace docs since 2026-09-03; a tenant
 * whose rows all predate that has no entry, and its dropdown label falls
 * back to the id. The filter value stays the id either way, so shared
 * `?client_tenant=` URLs keep working.
 */
export function tenantNames(
  items: readonly ReviewQueueItem[],
  metadataByKey: QueueClientMetadataIndex,
): Record<string, string> {
  const names: Record<string, string> = {}
  for (const item of items) {
    const row = metadataByKey[item.trace_key]
    const id = row?.client_tenant_id
    const name = row?.client_tenant_name
    if (id == null || id === '' || name == null || name === '') continue
    if (!(id in names)) names[id] = name
  }
  return names
}

export function metadataFilterLabel(
  filter: MetadataFilter,
  names: Readonly<Record<string, string>>,
): string {
  if (filter === ANY_METADATA_FILTER) return 'any'
  if (filter === NONE_METADATA_FILTER) return 'none'
  if (filter === UNKNOWN_METADATA_FILTER) return 'unknown'
  const value = filter.slice('value:'.length)
  return names[value] ?? value
}

/**
 * Sort displayed names first, then unnamed ids, then `unknown` last; keep
 * filter values intact.
 */
export function sortMetadataOptions(
  options: readonly MetadataFilter[],
  names: Readonly<Record<string, string>>,
): MetadataFilter[] {
  return [...options].sort((a, b) => {
    const unknownLast =
      Number(a === UNKNOWN_METADATA_FILTER) - Number(b === UNKNOWN_METADATA_FILTER)
    if (unknownLast) return unknownLast
    const aName = names[a.slice('value:'.length)]
    const bName = names[b.slice('value:'.length)]
    const namedFirst = Number(bName !== undefined) - Number(aName !== undefined)
    return namedFirst ||
      metadataFilterLabel(a, names).localeCompare(metadataFilterLabel(b, names)) ||
      a.localeCompare(b)
  })
}

export function withMetadataFilterParam(
  previous: URLSearchParams,
  name: string,
  filter: MetadataFilter,
): URLSearchParams {
  const next = new URLSearchParams(previous)
  if (filter === ANY_METADATA_FILTER) next.delete(name)
  else next.set(name, filter)
  return next
}
